/**
 * Возможности корзины
 *
 * Хранение: localStorage, ключ `carrotsbox_order`, формат `{ items: [...] }`.
 * Товар: `{ id, name, price, image, quantity, maxQuantity? }`.
 *
 * Публичный API — `window.CarrotsBoxCart`:
 *   getCart()              — полное состояние корзины
 *   getItems()             — массив товаров
 *   getCount()             — общее количество единиц
 *   getTotal()             — сумма заказа
 *   add(product)           — добавить товар (суммирует quantity при совпадении id)
 *   remove(id)             — удалить товар по id
 *   setQuantity(id, qty)   — изменить количество; при qty ≤ 0 — удаляет товар
 *   clear()                — очистить корзину
 *   open()                 — открыть попап оформления заказа
 *
 * Событие `carrotsbox:cart:updated` на document при любом изменении корзины.
 * detail: { cart, action, product?, id?, quantity? }, action: add | remove | setQuantity | clear.
 *
 * UI:
 *   — счётчик `.cart-button__count`, блок `.cart-block` (скрыт при пустой корзине)
 *   — уведомление о добавлении `.cart-list-modal` (3 с)
 *   — список товаров `.popup__product-list` и итог `.popup__product-total` в попапе заказа
 *   — кнопка `.cart-button` открывает попап, если корзина не пуста
 *
 * Разметка:
 *   `[data-target="add-to-cart"]`         — добавить товар + показать уведомление
 *   `[data-target="add-to-cart-one-click"]` — добавить товар + открыть попап заказа
 *   Данные товара берутся из `.single-product` или `.catalog-product-card` (data-product-*, поле `.single-product__quantity-field`).
 *   maxQuantity — из `data-product-max` (лимит заказа для товара).
 *
 * Оформление заказа:
 *   — попап `#cart-popup` через Fancybox
 *   — скрытое поле CF7 `your-products` синхронизируется JSON-составом заказа перед открытием
 *   — корзина очищается после успешной отправки формы (wpcf7mailsent)
 */
(function () {
	'use strict';

	const STORAGE_KEY = 'carrotsbox_order';
	const POPUP_SELECTOR = '#cart-popup';
	const NOTIFICATION_TIMEOUT = 3000;

	const getCart = () => {
		try {
			const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{"items":[]}');
			return Array.isArray(data.items) ? data : { items: [] };
		} catch {
			return { items: [] };
		}
	};

	const saveCart = (cart) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(cart));
	};

	const clearCart = () => {
		localStorage.removeItem(STORAGE_KEY);
	};

	const getTotalCount = (cart) => cart.items.reduce((sum, item) => sum + item.quantity, 0);

	const getTotalPrice = (cart) => cart.items.reduce((sum, item) => sum + item.price * item.quantity, 0);

	const formatPrice = (price) => {
		const value = Math.round(parseFloat(price) || 0);
		return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
	};

	const escapeHtml = (value) => {
		const node = document.createElement('div');
		node.textContent = value ?? '';
		return node.innerHTML;
	};

	const getMaxQuantity = (value) => {
		const max = parseInt(value, 10);
		return max > 0 ? max : null;
	};

	const clampQuantity = (quantity, max) => {
		const qty = Math.max(1, parseInt(quantity, 10) || 1);

		if (!max) {
			return qty;
		}

		return Math.min(max, qty);
	};

	const QUANTITY_MINUS_SVG = `
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M4.16672 9.99997L15.8333 9.99997" stroke="#D4D4D4" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
		</svg>
	`;

	const QUANTITY_PLUS_SVG = `
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path d="M15.8333 10L4.16672 10" stroke="#272729" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
			<path d="M9.99875 4.16672V15.8333" stroke="#272729" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
		</svg>
	`;

	const REMOVE_SVG = `
		<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M7.125 10.875L7.125 8.625" stroke="#272729" stroke-linecap="round" />
			<path d="M10.875 10.875L10.875 8.625" stroke="#272729" stroke-linecap="round" />
			<path d="M2.25 4.875H15.75C15.1689 4.875 14.8783 4.875 14.639 4.93494C13.9232 5.11425 13.3642 5.67319 13.1849 6.38904C13.125 6.62832 13.125 6.91888 13.125 7.5V10.625C13.125 12.5106 13.125 13.4534 12.5392 14.0392C11.9534 14.625 11.0106 14.625 9.125 14.625H8.875C6.98938 14.625 6.04657 14.625 5.46079 14.0392C4.875 13.4534 4.875 12.5106 4.875 10.625V7.5C4.875 6.91888 4.875 6.62832 4.81506 6.38904C4.63575 5.67319 4.07681 5.11425 3.36096 4.93494C3.12168 4.875 2.83112 4.875 2.25 4.875Z" stroke="#272729" stroke-linecap="round" />
			<path d="M7.125 2.62518C7.125 2.62518 7.5 1.875 9 1.875C10.5 1.875 10.875 2.625 10.875 2.625" stroke="#272729" stroke-linecap="round" />
		</svg>
	`;

	const emitCartUpdated = (action, detail = {}) => {
		document.dispatchEvent(
			new CustomEvent('carrotsbox:cart:updated', {
				detail: {
					cart: getCart(),
					action,
					...detail,
				},
			})
		);
	};

	const applyCartChange = (action, mutate, detail = {}) => {
		const cart = getCart();
		mutate(cart);
		saveCart(cart);
		updateCartUI();
		emitCartUpdated(action, detail);
		return getCart();
	};

	const addToCart = (cart, product) => {
		const maxQuantity = getMaxQuantity(product.maxQuantity);
		const quantity = clampQuantity(product.quantity, maxQuantity);
		const existing = cart.items.find((item) => String(item.id) === String(product.id));

		if (existing) {
			if (maxQuantity) {
				existing.maxQuantity = maxQuantity;
			}

			const itemMax = getMaxQuantity(existing.maxQuantity);
			existing.quantity = clampQuantity(existing.quantity + quantity, itemMax);
		} else {
			const item = { ...product, quantity };

			if (maxQuantity) {
				item.maxQuantity = maxQuantity;
			}

			cart.items.push(item);
		}
	};

	const removeFromCart = (cart, id) => {
		cart.items = cart.items.filter((item) => String(item.id) !== String(id));
	};

	const setItemQuantity = (cart, id, quantity) => {
		const item = cart.items.find((entry) => String(entry.id) === String(id));

		if (item) {
			item.quantity = clampQuantity(quantity, getMaxQuantity(item.maxQuantity));
		}
	};

	const buildProductsPayload = (cart) => ({
		items: cart.items.map((item) => {
			const payloadItem = {
				id: item.id,
				name: item.name,
				quantity: item.quantity,
				price: item.price,
				lineTotal: item.price * item.quantity,
			};

			if (item.maxQuantity) {
				payloadItem.maxQuantity = item.maxQuantity;
			}

			return payloadItem;
		}),
		total: getTotalPrice(cart),
	});

	const syncYourProductsField = () => {
		const popup = document.querySelector(POPUP_SELECTOR);
		if (!popup) {
			return;
		}

		const input = popup.querySelector('input[name="your-products"]');
		if (!input) {
			return;
		}

		const payload = buildProductsPayload(getCart());
		input.value = JSON.stringify(payload);
		input.dispatchEvent(new Event('input', { bubbles: true }));
		input.dispatchEvent(new Event('change', { bubbles: true }));
	};

	const isOrderPopupOpen = () => {
		if (typeof Fancybox === 'undefined') {
			return false;
		}

		const instance = Fancybox.getInstance?.();

		if (!instance) {
			return false;
		}

		const slide = instance.getSlide?.();
		return slide?.src === POPUP_SELECTOR;
	};

	const closeOrderPopupIfEmpty = () => {
		if (typeof Fancybox === 'undefined') {
			return;
		}

		if (getTotalCount(getCart()) === 0 && isOrderPopupOpen()) {
			Fancybox.close();
		}
	};

	const openOrderPopup = () => {
		if (getTotalCount(getCart()) === 0) {
			closeOrderPopupIfEmpty();
			return;
		}

		updateCartProductList();
		syncYourProductsField();

		if (typeof Fancybox === 'undefined') {
			return;
		}

		Fancybox.show([{ src: POPUP_SELECTOR, type: 'inline' }]);
	};

	const renderCartProductPreview = (product) => {
		const imageHtml = product.image
			? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.name)}" loading="lazy">`
			: '';

		return `
			<div class="cart-product">
				<div class="cart-product__name">${escapeHtml(product.name)}</div>
				<div class="cart-product__price">${formatPrice(product.price * product.quantity)}</div>
				<div class="cart-product__img-wrapper">${imageHtml}</div>
			</div>
		`;
	};

	const renderCartProductListItem = (item) => {
		const imageHtml = item.image
			? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.name)}" loading="lazy">`
			: '';

		const maxQuantityAttr = item.maxQuantity ? ` data-product-max="${escapeHtml(String(item.maxQuantity))}"` : '';
		const quantityMaxAttr = item.maxQuantity ? ` data-max="${escapeHtml(String(item.maxQuantity))}"` : '';

		return `
			<article
				class="popup-cart-item"
				data-product-id="${escapeHtml(String(item.id))}"
				data-product-name="${escapeHtml(item.name)}"
				data-product-price="${escapeHtml(String(item.price))}"
				data-product-image="${escapeHtml(item.image)}"${maxQuantityAttr}
			>
				<div class="popup-cart-item__body">
					<div class="popup-cart-item__name">${escapeHtml(item.name)}</div>

					<div class="popup-cart-item__bottom">
						<div class="popup-cart-item__price-wrapper">
							<div class="popup-cart-item__price">${formatPrice(item.price)}</div>
							<div class="popup-cart-item__quantity"${quantityMaxAttr}>
								<button type="button" class="popup-cart-item__quantity-btn popup-cart-item__quantity-btn--minus" data-cart-qty="minus" aria-label="Уменьшить количество">
									${QUANTITY_MINUS_SVG}
								</button>
								<input type="text" class="popup-cart-item__quantity-field" data-cart-qty="field" value="${item.quantity}" inputmode="numeric" aria-label="Количество">
								<button type="button" class="popup-cart-item__quantity-btn popup-cart-item__quantity-btn--plus" data-cart-qty="plus" aria-label="Увеличить количество">
									${QUANTITY_PLUS_SVG}
								</button>
							</div>
						</div>
					
						<button type="button" class="popup-cart-item__remove" data-cart-remove aria-label="Удалить товар">
							${REMOVE_SVG}
						</button>
					</div>
				</div>
				<div class="popup-cart-item__img">${imageHtml}</div>
			</article>
		`;
	};

	const renderCartProductList = (cart) => {
		if (!cart.items.length) {
			return '<p class="popup__product-list-empty">Список товаров пуст</p>';
		}

		return cart.items.map(renderCartProductListItem).join('');
	};

	const updateCartProductTotal = () => {
		const totalEl = document.querySelector(`${POPUP_SELECTOR} .popup__product-total`);

		if (!totalEl) {
			return;
		}

		const cart = getCart();

		if (!cart.items.length) {
			totalEl.innerHTML = '';
			totalEl.hidden = true;
			return;
		}

		totalEl.hidden = false;
		totalEl.innerHTML = `
			<span class="popup__product-total-label">Итого:</span>
			<span class="popup__product-total-value">${formatPrice(getTotalPrice(cart))}</span>
		`;
	};

	const updateCartProductList = () => {
		const list = document.querySelector(`${POPUP_SELECTOR} .popup__product-list`);

		if (list) {
			list.innerHTML = renderCartProductList(getCart());
		}

		updateCartProductTotal();
	};

	let notificationTimer = null;

	const showAddedNotification = (product) => {
		const modal = document.querySelector('.cart-list-modal');
		const productContainer = document.querySelector('.cart-list-modal__product');

		if (!modal || !productContainer) {
			return;
		}

		productContainer.innerHTML = renderCartProductPreview(product);
		modal.hidden = false;
		modal.style.visibility = 'visible';
		modal.style.opacity = '1';

		clearTimeout(notificationTimer);
		notificationTimer = setTimeout(() => {
			modal.hidden = true;
			modal.style.visibility = '';
			modal.style.opacity = '';
		}, NOTIFICATION_TIMEOUT);
	};

	const updateCartUI = () => {
		const cartBlock = document.querySelector('.cart-block');
		const countEl = document.querySelector('.cart-button__count');
		const cart = getCart();
		const totalCount = getTotalCount(cart);

		if (countEl) {
			countEl.textContent = String(totalCount);
		}

		if (cartBlock) {
			cartBlock.hidden = totalCount === 0;
			cartBlock.classList.toggle('is-visible', totalCount > 0);
		}

		updateCartProductList();
		closeOrderPopupIfEmpty();
	};

	const getProductFromSection = (section) => {
		const quantityInput = section.querySelector('.single-product__quantity-field');
		const quantity = parseInt(quantityInput?.value, 10) || 1;
		const maxQuantity = getMaxQuantity(section.dataset.productMax);

		return {
			id: section.dataset.productId,
			name: section.dataset.productName || '',
			price: parseFloat(section.dataset.productPrice) || 0,
			image: section.dataset.productImage || '',
			quantity,
			...(maxQuantity ? { maxQuantity } : {}),
		};
	};

	const resolveItemQuantity = (itemEl, quantity) => {
		const max = getMaxQuantity(itemEl.dataset.productMax);
		return clampQuantity(quantity, max);
	};

	const initAddToCartButtons = () => {
		document.querySelectorAll('[data-target="add-to-cart"]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();

				const section = button.closest('.single-product, .catalog-product-card');
				if (!section) {
					return;
				}

				const product = getProductFromSection(section);
				window.CarrotsBoxCart.add(product);
				showAddedNotification(product);
			});
		});

		document.querySelectorAll('[data-target="add-to-cart-one-click"]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();

				const section = button.closest('.single-product, .catalog-product-card');

				if (!section) {
					return;
				}

				const product = getProductFromSection(section);
				window.CarrotsBoxCart.add(product);
				window.CarrotsBoxCart.open();
			});
		});
	};

	const initCartProductListControls = () => {
		const list = document.querySelector(`${POPUP_SELECTOR} .popup__product-list`);

		if (!list) {
			return;
		}

		list.addEventListener('click', (event) => {
			const removeBtn = event.target.closest('[data-cart-remove]');

			if (removeBtn) {
				const item = removeBtn.closest('[data-product-id]');

				if (item) {
					window.CarrotsBoxCart.remove(item.dataset.productId);
					syncYourProductsField();
				}

				return;
			}

			const item = event.target.closest('[data-product-id]');

			if (!item) {
				return;
			}

			const minusBtn = event.target.closest('[data-cart-qty="minus"]');
			const plusBtn = event.target.closest('[data-cart-qty="plus"]');

			if (!minusBtn && !plusBtn) {
				return;
			}

			const field = item.querySelector('[data-cart-qty="field"]');
			let quantity = parseInt(field?.value, 10) || 1;
			quantity = minusBtn ? quantity - 1 : quantity + 1;

			if (quantity <= 0) {
				window.CarrotsBoxCart.remove(item.dataset.productId);
				syncYourProductsField();
				return;
			}

			quantity = resolveItemQuantity(item, quantity);

			window.CarrotsBoxCart.setQuantity(item.dataset.productId, quantity);
			syncYourProductsField();
		});

		list.addEventListener('input', (event) => {
			const field = event.target.closest('[data-cart-qty="field"]');

			if (field) {
				field.value = field.value.replace(/\D/g, '');
			}
		});

		list.addEventListener(
			'blur',
			(event) => {
				const field = event.target.closest('[data-cart-qty="field"]');

				if (!field) {
					return;
				}

				const item = field.closest('[data-product-id]');

				if (!item) {
					return;
				}

				let quantity = parseInt(field.value, 10);

				if (!quantity || quantity <= 0) {
					window.CarrotsBoxCart.remove(item.dataset.productId);
					syncYourProductsField();
					return;
				}

				quantity = resolveItemQuantity(item, quantity);

				window.CarrotsBoxCart.setQuantity(item.dataset.productId, quantity);
				syncYourProductsField();
			},
			true
		);
	};

	const initOrderFormSubmit = () => {
		const form = document.querySelector(`${POPUP_SELECTOR} .wpcf7`);
		
		if (!form) {
			return;
		}

		form.addEventListener('wpcf7mailsent', () => {
			window.CarrotsBoxCart.clear();
		});
	};

	const initCartButton = () => {
		const cartButton = document.querySelector('.cart-button');
		if (!cartButton) {
			return;
		}

		const open = () => {
			if (getTotalCount(getCart()) === 0) {
				return;
			}

			openOrderPopup();
		};

		cartButton.addEventListener('click', open);
		cartButton.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				open();
			}
		});
	};

	window.CarrotsBoxCart = {
		getCart,
		getItems: () => getCart().items,
		getCount: () => getTotalCount(getCart()),
		getTotal: () => getTotalPrice(getCart()),

		add(product) {
			return applyCartChange('add', (cart) => addToCart(cart, product), { product });
		},

		remove(id) {
			return applyCartChange('remove', (cart) => removeFromCart(cart, id), { id });
		},

		setQuantity(id, quantity) {
			const qty = parseInt(quantity, 10);

			if (!qty || qty <= 0) {
				return this.remove(id);
			}

			return applyCartChange('setQuantity', (cart) => setItemQuantity(cart, id, qty), { id, quantity: qty });
		},

		clear() {
			clearCart();
			updateCartUI();
			emitCartUpdated('clear');
			return getCart();
		},

		open() {
			openOrderPopup();
		},
	};

	document.addEventListener('DOMContentLoaded', () => {
		initAddToCartButtons();
		initCartButton();
		initCartProductListControls();
		initOrderFormSubmit();
		updateCartUI();
	});
})();
